import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AuthContext, LanguageContext, API, formatDateWithEnglishNumerals } from '../shared';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Edit, Search, Trash2, Download } from 'lucide-react';

// Payments Management Component
const PaymentsManagement = () => {
  const { t } = useContext(LanguageContext);
  const [payments, setPayments] = useState([]);
  const [operations, setOperations] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [paymentFilter, setPaymentFilter] = useState('all'); // all, today, this_week, this_month
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const [formData, setFormData] = useState({
    invoice_id: '',
    method: 'cash',
    amount: '',
    payment_date: new Date().toISOString().split('T')[0]
  });
  const [exporting, setExporting] = useState(false);

  const handleExport = async (format = 'csv') => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      params.append('payment_type', 'operation');
      if (searchTerm) params.append('search', searchTerm);

      const today = new Date();
      if (paymentFilter === 'today') {
        const t = today.toISOString().split('T')[0];
        params.append('from_date', t);
        params.append('to_date', t);
      } else if (paymentFilter === 'this_week') {
        const start = new Date(today); start.setDate(today.getDate() - 7);
        params.append('from_date', start.toISOString().split('T')[0]);
      } else if (paymentFilter === 'this_month') {
        const start = new Date(today.getFullYear(), today.getMonth(), 1);
        params.append('from_date', start.toISOString().split('T')[0]);
      }

      const endpoint = format === 'xlsx' ? 'payments/export-xlsx' : 'payments/export';
      const ext = format === 'xlsx' ? 'xlsx' : 'csv';
      const mime = format === 'xlsx'
        ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        : 'text/csv;charset=utf-8';

      const r = await axios.get(`${API}/${endpoint}?${params}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: mime }));
      const a = document.createElement('a');
      a.href = url;
      const ts = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      a.download = `payments_${ts}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export failed:', err);
      alert('فشل التصدير. حاول مجدداً.');
    } finally {
      setExporting(false);
    }
  };

  const handleExportCSV = () => handleExport('csv');
  const handleExportXLSX = () => handleExport('xlsx');

  const fetchData = async () => {
    try {
      const [paymentsRes, operationsRes, clientsRes] = await Promise.all([
        // Use the new paginated endpoint — request a generous first batch.
        // For huge datasets later, swap to infinite-scroll using `page` param.
        axios.get(`${API}/payments?payment_type=operation&page=1&per_page=200`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }),
        axios.get(`${API}/daily-operations`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }),
        axios.get(`${API}/clients`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        })
      ]);

      // New envelope: { data: [...], pagination: {...} }. Old shape (plain array)
      // is still supported for safety (older deployed cache might serve old shape).
      const paymentsList = Array.isArray(paymentsRes.data)
        ? paymentsRes.data
        : (paymentsRes.data?.data || []);

      // Sort by payment_date desc (server already sorts; this is a safety net)
      const sortedPayments = [...paymentsList].sort((a, b) =>
        new Date(b.payment_date || b.created_at) - new Date(a.payment_date || a.created_at)
      );

      setPayments(sortedPayments);
      setOperations(operationsRes.data);
      setClients(clientsRes.data);
    } catch (error) {
      console.error('Error fetching payment data:', error);
      if (error.response?.status === 403) {
        alert('ليس لديك صلاحية للوصول إلى المدفوعات');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const paymentData = {
        invoice_id: formData.invoice_id,
        payment_method: formData.method,
        amount: parseFloat(formData.amount),
        payment_date: formData.payment_date
      };

      if (editingPayment) {
        await axios.put(`${API}/payments/${editingPayment.id}`, paymentData);
      } else {
        await axios.post(`${API}/payments`, paymentData);
      }
      
      fetchData();
      setIsDialogOpen(false);
      setEditingPayment(null);
      setFormData({ invoice_id: '', method: '', amount: '', payment_date: '' });
    } catch (error) {
      console.error('Error saving payment:', error);
    }
  };

  const handleEdit = (payment) => {
    setEditingPayment(payment);
    setFormData({
      invoice_id: payment.invoice_id,
      method: payment.payment_method,
      amount: payment.amount.toString(),
      payment_date: payment.payment_date
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (paymentId) => {
    if (window.confirm(t('confirmDelete'))) {
      try {
        await axios.delete(`${API}/payments/${paymentId}`);
        fetchData();
      } catch (error) {
        console.error('Error deleting payment:', error);
      }
    }
  };

  const getOperationInfo = (operationId) => {
    const operation = operations.find(op => op.id === operationId);
    if (operation) {
      const client = clients.find(c => c.id === operation.client_id);
      return {
        operation_no: operation.operation_no,
        service_name: operation.service_name,
        client_name: client?.name || 'غير معروف',
        final_price: operation.final_price
      };
    }
    return {
      operation_no: 'غير معروف',
      service_name: 'غير معروف', 
      client_name: 'غير معروف',
      final_price: 0
    };
  };

  const getMethodBadge = (method) => {
    const methodColors = {
      'cash': 'bg-green-600',
      'bank': 'bg-brand-600',
      'card': 'bg-purple-600'
    };
    
    return (
      <Badge className={`${methodColors[method] || 'bg-gray-600'} text-white`}>
        {t(method)}
      </Badge>
    );
  };

  const filteredPayments = payments.filter(payment => {
    if (!payment.daily_operation_id) return false; // Only show operation payments
    
    const operationInfo = getOperationInfo(payment.daily_operation_id);
    const searchLower = searchTerm.toLowerCase();
    
    return (
      payment.payment_no.toLowerCase().includes(searchLower) ||
      operationInfo.operation_no.toLowerCase().includes(searchLower) ||
      operationInfo.client_name.toLowerCase().includes(searchLower) ||
      operationInfo.service_name.toLowerCase().includes(searchLower)
    );
  });

  if (loading) {
    return <div className="text-center py-8">{t('loading')}</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">💰 مدفوعات العمليات</h2>
          <p className="text-sm text-gray-600 mt-1">إدارة مدفوعات العملاء للعمليات اليومية - سداد مباشر بدون فواتير</p>
        </div>
        <div className="bg-brand-100 text-brand-800 px-4 py-2 rounded-lg">
          <p className="text-sm">
            💡 <strong>لإضافة مدفوعات جديدة:</strong> انتقل إلى صفحة "العمليات اليومية" واضغط على زر "💰 إضافة دفعة" بجانب العملية المطلوبة
          </p>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
          <Input
            placeholder={t('search') + '...'}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="payments-search-input"
          />
        </div>
        <Button
          onClick={handleExportXLSX}
          disabled={exporting || payments.length === 0}
          className="bg-emerald-600 hover:bg-emerald-700 text-white whitespace-nowrap"
          data-testid="payments-export-xlsx-btn"
        >
          <Download className="h-4 w-4 ml-2" />
          {exporting ? '...' : 'تصدير Excel'}
        </Button>
        <Button
          onClick={handleExportCSV}
          disabled={exporting || payments.length === 0}
          className="bg-brand-600 hover:bg-brand-700 text-white whitespace-nowrap"
          data-testid="payments-export-csv-btn"
        >
          <Download className="h-4 w-4 ml-2" />
          {exporting ? '...جاري التصدير' : 'تصدير CSV'}
        </Button>
      </div>

      <Card>
        <CardContent>
          <div className="mb-4">
            <Input
              placeholder={t('search') + '...'}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-right">رقم المدفوعة</TableHead>
                <TableHead className="text-right">رقم العملية</TableHead>
                <TableHead className="text-right">اسم العميل</TableHead>
                <TableHead className="text-right">الخدمة</TableHead>
                <TableHead className="text-right">طريقة الدفع</TableHead>
                <TableHead className="text-right">المبلغ المدفوع</TableHead>
                <TableHead className="text-right">تاريخ الدفع</TableHead>
                <TableHead className="text-right">الإجراءات</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredPayments.map((payment) => {
                const operationInfo = getOperationInfo(payment.daily_operation_id);
                return (
                  <TableRow key={payment.id}>
                    <TableCell className="text-right font-medium text-brand-700">{payment.payment_no}</TableCell>
                    <TableCell className="text-right">{operationInfo.operation_no}</TableCell>
                    <TableCell className="text-right">{operationInfo.client_name}</TableCell>
                    <TableCell className="text-right">{operationInfo.service_name}</TableCell>
                    <TableCell className="text-right">{getMethodBadge(payment.method)}</TableCell>
                    <TableCell className="text-right font-bold text-green-700">{payment.amount.toLocaleString()} دج</TableCell>
                    <TableCell className="text-right">{formatDateWithEnglishNumerals(payment.payment_date)}</TableCell>
                    <TableCell>
                      <div className="flex space-x-2">
                        <Button
                          variant="outline"
                          size="sm"
                          title="🧾 وصل دفع PDF"
                          className="text-brand-600 hover:bg-brand-50"
                          data-testid={`print-payment-pdf-${payment.id}`}
                          onClick={() => {
                            const t = localStorage.getItem('token');
                            window.open(
                              `${API}/daily-operations/${payment.daily_operation_id}/print?token=${encodeURIComponent(t)}`,
                              '_blank',
                            );
                          }}
                        >
                          🖨️
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          title="📊 وصل دفع Excel"
                          className="text-emerald-600 hover:bg-emerald-50"
                          data-testid={`export-payment-xlsx-${payment.id}`}
                          onClick={() => {
                            const t = localStorage.getItem('token');
                            const a = document.createElement('a');
                            a.href = `${API}/payments/${payment.id}/xlsx?token=${encodeURIComponent(t)}`;
                            a.download = `receipt_${payment.payment_no || payment.id}.xlsx`;
                            document.body.appendChild(a);
                            a.click();
                            document.body.removeChild(a);
                          }}
                        >
                          📊
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEdit(payment)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => handleDelete(payment.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

// Enhanced Reports Management Component with Agency Breakdown

export default PaymentsManagement;
